import React from "react";
import {
  FileText,
  Sun,
  Heart,
  Home,
  AlertCircle,
  Briefcase,
} from "lucide-react";
import { getStatusColor, getStatusIcon, calculateDuration } from "../utils/leaveHelpers";

// ===== SINGLE LEAVE ITEM (collapsible) =====
const LeaveItem = ({
  leave,
  expandedLeaveId,
  setExpandedLeaveId,
  isDarkMode,
  fonts,
  theme,
  language,
  t,
  accentColor,
}) => {
  const isExpanded = expandedLeaveId === leave.id;
  const StatusIcon = getStatusIcon(leave.status);
  const duration =
    leave.duration ?? calculateDuration(leave.startDate, leave.endDate);

  return (
    <div
      style={{
        marginBottom: "12px",
        borderRadius: "10px",
        background: isDarkMode
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.02)",
        border: isDarkMode
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        transition: "all 0.3s ease",
      }}
    >
      {/* Header - Always Visible */}
      <div
        onClick={() => setExpandedLeaveId(isExpanded ? null : leave.id)}
        style={{
          padding: "12px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "4px",
            }}
          >
            <span
              style={{
                color: isDarkMode ? "white" : "#1a1a1a",
                fontSize: fonts.small,
                fontWeight: "600",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {new Date(leave.startDate).toLocaleDateString(
                language === "ar" ? "ar-EG" : "en-US",
                { month: "short", day: "numeric" }
              )}
              {" - "}
              {new Date(leave.endDate).toLocaleDateString(
                language === "ar" ? "ar-EG" : "en-US",
                { month: "short", day: "numeric" }
              )}
            </span>
            <span
              style={{
                color: accentColor,
                fontSize: fonts.tiny,
                fontWeight: "600",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              • {duration} {duration > 1 ? t.units.days : t.units.day}
            </span>
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "2px 8px",
              background: `${getStatusColor(leave.status, theme, isDarkMode)}20`,
              borderRadius: "6px",
            }}
          >
            {React.createElement(StatusIcon, {
              size: 12,
              color: getStatusColor(leave.status, theme, isDarkMode),
            })}
            <span
              style={{
                color: getStatusColor(leave.status, theme, isDarkMode),
                fontSize: fonts.tiny,
                fontWeight: "600",
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {t.status[leave.status] || leave.status}
            </span>
          </div>
        </div>
        <div
          style={{
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        >
          <AlertCircle
            size={16}
            color={
              isDarkMode
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(26, 26, 26, 0.6)"
            }
          />
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div
          style={{
            padding: "0 12px 12px 12px",
            borderTop: isDarkMode
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.05)",
          }}
        >
          {[
            { label: t.details.reason, value: leave.reason },
            { label: t.details.handover, value: leave.handover },
            { label: t.details.approvedBy, value: leave.approvedBy },
          ].map(
            ({ label, value }) =>
              value && (
                <div key={label} style={{ marginTop: "12px" }}>
                  <p
                    style={{
                      color: isDarkMode
                        ? "rgba(255, 255, 255, 0.6)"
                        : "rgba(26, 26, 26, 0.6)",
                      fontSize: fonts.tiny,
                      fontWeight: "600",
                      margin: "0 0 4px 0",
                      textTransform: "uppercase",
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {label}
                  </p>
                  <p
                    style={{
                      color: isDarkMode ? "white" : "#1a1a1a",
                      fontSize: fonts.small,
                      margin: 0,
                      fontFamily: "'Cairo', sans-serif",
                    }}
                  >
                    {value}
                  </p>
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

// ===== LEAVE TYPE SECTION CARD =====
const LeaveTypeSection = ({
  id,
  icon: Icon,
  title,
  gradient,
  shadowColor,
  accentColor,
  leaves,
  expandedLeaveId,
  setExpandedLeaveId,
  isDarkMode,
  screenSize,
  fonts,
  theme,
  language,
  t,
}) => {
  const filtered = leaves.filter(
    (l) => (l.leaveType || l.type) === id
  );
  const approved = filtered.filter((l) => l.status === "approved").length;
  const pending = filtered.filter((l) => l.status === "pending").length;
  const rejected = filtered.filter((l) => l.status === "rejected").length;

  return (
    <div
      style={{
        background: isDarkMode
          ? "rgba(255, 255, 255, 0.03)"
          : "rgba(255, 255, 255, 0.5)",
        borderRadius: screenSize === "xs" ? "12px" : "16px",
        padding: screenSize === "xs" ? "16px" : "20px",
        border: isDarkMode
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
          paddingBottom: "12px",
          borderBottom: isDarkMode
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            width: screenSize === "xs" ? "40px" : "48px",
            height: screenSize === "xs" ? "40px" : "48px",
            background: gradient,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${shadowColor}`,
          }}
        >
          {React.createElement(Icon, {
            size: screenSize === "xs" ? 20 : 24,
            color: "#ffffff",
          })}
        </div>
        <div style={{ flex: 1 }}>
          <h4
            style={{
              color: isDarkMode ? "white" : "#1a1a1a",
              fontSize: screenSize === "xs" ? fonts.body : fonts.subtitle,
              fontWeight: "600",
              margin: "0 0 8px 0",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {title}
          </h4>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {approved > 0 && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  background: `${theme.colors.success}20`,
                  color: theme.colors.success,
                  fontSize: fonts.tiny,
                  fontWeight: "600",
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                ✓ {approved}
              </span>
            )}
            {pending > 0 && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  background: `${theme.colors.warning}20`,
                  color: theme.colors.warning,
                  fontSize: fonts.tiny,
                  fontWeight: "600",
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                ⏱ {pending}
              </span>
            )}
            {rejected > 0 && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  background: `${theme.colors.danger}20`,
                  color: theme.colors.danger,
                  fontSize: fonts.tiny,
                  fontWeight: "600",
                  fontFamily: "'Cairo', sans-serif",
                }}
              >
                ✕ {rejected}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Leave Items */}
      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <p
            style={{
              textAlign: "center",
              color: isDarkMode
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(26, 26, 26, 0.5)",
              fontSize: fonts.small,
              padding: "20px",
              fontFamily: "'Cairo', sans-serif",
            }}
          >
            {t.history.noRecentRequests}
          </p>
        ) : (
          filtered.map((leave) => (
            <LeaveItem
              key={leave.id}
              leave={leave}
              expandedLeaveId={expandedLeaveId}
              setExpandedLeaveId={setExpandedLeaveId}
              isDarkMode={isDarkMode}
              fonts={fonts}
              theme={theme}
              language={language}
              t={t}
              accentColor={accentColor}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ===== MAIN LEAVE HISTORY TAB =====
const LeaveHistoryTab = ({
  leaveHistory,
  leaveTypes,
  expandedLeaveId,
  setExpandedLeaveId,
  isDarkMode,
  screenSize,
  fonts,
  spacing,
  theme,
  language,
  t,
}) => {
  const sections = [
    {
      id: "annual",
      icon: Sun,
      title: t.leaveTypes.annual,
      gradient: `linear-gradient(135deg, ${theme.colors.warning}, #f97316)`,
      shadowColor: `${theme.colors.warning}30`,
      accentColor: theme.colors.warning,
    },
    {
      id: "sick",
      icon: Heart,
      title: t.leaveTypes.sick,
      gradient: `linear-gradient(135deg, ${theme.colors.danger}, #dc2626)`,
      shadowColor: `${theme.colors.danger}30`,
      accentColor: theme.colors.danger,
    },
    {
      id: "maternity",
      icon: Home,
      title: t.leaveTypes.maternity,
      gradient: `linear-gradient(135deg, ${theme.colors.success}, #059669)`,
      shadowColor: `${theme.colors.success}30`,
      accentColor: theme.colors.success,
    },
    {
      id: "emergency",
      icon: AlertCircle,
      title: t.leaveTypes.emergency,
      gradient: `linear-gradient(135deg, ${theme.colors.info}, #0891b2)`,
      shadowColor: `${theme.colors.info}30`,
      accentColor: theme.colors.info,
    },
    {
      id: "wfh",
      icon: Briefcase,
      title: t.leaveTypes.workFromHome,
      gradient: `linear-gradient(135deg, #2e7cba, #2e7cba)`,
      shadowColor: `#2e7cba30`,
      accentColor: "#2e7cba",
    },
  ];

  const cardStyle = {
    background: isDarkMode
      ? "rgba(255, 255, 255, 0.01)"
      : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(1.5625rem)",
    WebkitBackdropFilter: "blur(1.5625rem)",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.05)",
    borderRadius: screenSize === "xs" ? "1rem" : "1.25rem",
    padding: spacing.padding,
    boxShadow: isDarkMode
      ? "0 0.5rem 2rem rgba(0, 0, 0, 0.2)"
      : "0 0.5rem 2rem rgba(0, 0, 0, 0.1)",
    marginBottom: spacing.margin,
    transition: "all 0.3s ease",
  };

  return (
    <div style={cardStyle}>
      <h3
        style={{
          color: isDarkMode ? "white" : "#1a1a1a",
          fontSize: fonts.subtitle,
          fontWeight: "600",
          margin: `0 0 ${spacing.margin} 0`,
          display: "flex",
          alignItems: "center",
          gap: screenSize === "xs" ? "0.5rem" : "0.5rem",
          fontFamily: "'Cairo', sans-serif",
        }}
      >
        <FileText
          size={screenSize === "xs" ? 20 : 24}
          color={theme.colors.primary}
        />
        {t.history.title}
      </h3>

      {leaveHistory.length === 0 ? (
        <div
          style={{
            padding: screenSize === "xs" ? "32px 16px" : "40px 24px",
            textAlign: "center",
            color: isDarkMode
              ? "rgba(255, 255, 255, 0.6)"
              : "rgba(26, 26, 26, 0.6)",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          <FileText size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
          <p style={{ margin: 0, fontSize: fonts.body }}>{t.history.noHistory}</p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              screenSize === "xs"
                ? "1fr"
                : screenSize === "sm"
                ? "1fr"
                : screenSize === "md"
                ? "repeat(2, 1fr)"
                : "repeat(3, 1fr)",
            gap: screenSize === "xs" ? "16px" : "24px",
          }}
        >
          {sections.map((section) =>
            leaveTypes.find((lt) => lt.id === section.id) ? (
              <LeaveTypeSection
                key={section.id}
                {...section}
                leaves={leaveHistory}
                expandedLeaveId={expandedLeaveId}
                setExpandedLeaveId={setExpandedLeaveId}
                isDarkMode={isDarkMode}
                screenSize={screenSize}
                fonts={fonts}
                theme={theme}
                language={language}
                t={t}
              />
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default LeaveHistoryTab;